export default class BottomCircle {
  constructor({
    activeClass = 'is-active',
    reverseClass = 'js-kv_reverse',
    bottomCircleSelector = '.js-kv_bottom_circle',
    bottomCirclePathDurationMs = 100,
    bottomCircleTriggerOffsetFromBottom = 0.2
  } = {}) {
    this.activeClass = activeClass;
    this.reverseClass = reverseClass;
    this.bottomCircleSelector = bottomCircleSelector;
    this.bottomCirclePathDurationMs = bottomCirclePathDurationMs;
    this.bottomCircleTriggerOffsetFromBottom = bottomCircleTriggerOffsetFromBottom;
    this.bottomCirclePreparedMap = new WeakMap();
    this.bottomCircleSvgs = document.querySelectorAll(this.bottomCircleSelector);
  }

  init() {
    if (!this.bottomCircleSvgs.length) {
      return;
    }

    this.prepareBottomCircleSvgs();
    this.observeBottomCircleSvgs();
  }

  prepareBottomCircleSvgs() {
    this.bottomCircleSvgs.forEach((svg) => this.prepareBottomCircle(svg));
  }

  prepareBottomCircle(svg) {
    const paths = Array.from(svg.querySelectorAll('path'));
    if (!paths.length) {
      return;
    }

    const pathMeta = paths.map((path) => {
      const dash = path.getTotalLength();
      const isReverse = path.classList.contains(this.reverseClass);
      const startOffset = isReverse ? -dash : dash;
      path.style.strokeDasharray = `${dash} ${dash}`;
      path.style.strokeDashoffset = `${startOffset}`;
      path.style.opacity = '0';
      return { dash, isReverse };
    });

    this.bottomCirclePreparedMap.set(svg, { paths, pathMeta });
  }

  observeBottomCircleSvgs() {
    if (!('IntersectionObserver' in window)) {
      this.bottomCircleSvgs.forEach((svg) => this.animateBottomCircle(svg));
      return;
    }

    const rootMarginBottom = `-${Math.floor(this.bottomCircleTriggerOffsetFromBottom * 100)}%`;
    const observer = new IntersectionObserver(
      (entries, currentObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          const svg = entry.target;
          currentObserver.unobserve(svg);
          this.animateBottomCircle(svg);
        });
      },
      {
        root: null,
        rootMargin: `0px 0px ${rootMarginBottom} 0px`,
        threshold: 0
      }
    );

    this.bottomCircleSvgs.forEach((svg) => observer.observe(svg));
  }

  animateBottomCircle(svg) {
    const prepared = this.bottomCirclePreparedMap.get(svg);
    if (!prepared) {
      return;
    }

    const { paths, pathMeta } = prepared;
    svg.classList.add(this.activeClass);

    const pathDurationMs = this.bottomCirclePathDurationMs;
    const durations = pathMeta.map(() => pathDurationMs);
    const delays = [];
    let totalDelay = 0;

    for (let i = 0; i < durations.length; i += 1) {
      delays[i] = totalDelay;
      totalDelay += durations[i];
    }

    requestAnimationFrame(() => {
      paths.forEach((path, index) => {
        const { dash, isReverse } = pathMeta[index];
        path.style.opacity = '1';
        path.animate(
          [
            { opacity: 1, strokeDashoffset: isReverse ? -dash : dash },
            { opacity: 1, strokeDashoffset: 0 }
          ],
          {
            duration: Math.max(1, Math.floor(durations[index])),
            delay: Math.floor(delays[index]),
            easing: 'ease-in-out',
            fill: 'forwards'
          }
        );
      });
    });
  }
}
